// ---------------------------------------------------------------------------
// brendaCharges — result composition
// ---------------------------------------------------------------------------
//
// Composes the final `BrendaChargesResult` from a raw charges response.
// The heavy lifting (pricing, stock rules, branch selection, display
// mapping) lives in dedicated siblings:
//
//   - `brendaPrices.ts`   — price derivation from itemTotals
//   - `brendaStock.ts`    — delivery / pickup stock rules + branch picking
//   - `brendaMapping.ts`  — display-shape mapping (main product, linked,
//                           cross-sell, pickup points, product-type infer)
//
// This file only knows how to fold those pieces into the consumer-facing
// `BrendaChargesResult`, so the hook has a single entry point to call.
// ---------------------------------------------------------------------------

// App imports
import {
  checkBrendaDaysBetween,
  chooseBrendaHireEndDate,
} from '~/utils/brendaDates'
import { getBrendaDeliveryPrice } from '~/utils/brendaPrices'
import {
  findBrendaNearestPickup,
  findBrendaPickupByBranchId,
  formatBrendaBranchAddress,
  formatBrendaBranchDistance,
  resolveBrendaDeliveryStock,
  resolveBrendaPickupStock,
} from '~/utils/brendaStock'
import {
  inferBrendaProductType,
  mapBrendaChargesData,
  mapBrendaLinkedStock,
} from '~/utils/brendaMapping'

// Types
import type { GetPricesHireData, HssBankHolidayItem } from '~/types'
import type {
  BrendaChargesDelivery,
  BrendaChargesEvaluation,
  BrendaChargesPickup,
  BrendaChargesResult,
  BrendaChargesSubmit,
} from '~/types/brendaCharges'

// ---------------------------------------------------------------------------
// Delivery resolution
// ---------------------------------------------------------------------------

/**
 * Resolves the delivery side of a charges response into a single
 * availability summary: `{ available, date, price, maxQuantity }`.
 */
export const resolveBrendaChargesDelivery = (
  charges: GetPricesHireData,
  request: BrendaChargesSubmit
): BrendaChargesDelivery => {
  const item = charges.item
  const productType = inferBrendaProductType(charges)

  const siteMax = item?.siteWithStock?.maxQuantity
  const deliveryStock = typeof siteMax === 'number' ? siteMax : null

  const stock = resolveBrendaDeliveryStock(
    productType,
    deliveryStock,
    item?.deliveryDate ?? null,
    item?.firstAvailableDate ?? null,
    request.startDate
  )

  const price = getBrendaDeliveryPrice(item?.itemTotals)

  return {
    available: stock.available,
    date: stock.available ? stock.date : null,
    price: stock.available ? (price ?? null) : null,
    maxQuantity: stock.maxQuantity,
  }
}

// ---------------------------------------------------------------------------
// Pickup resolution
// ---------------------------------------------------------------------------

/**
 * Resolves the pickup side of a charges response into a branch +
 * availability summary. Branch selection follows the "keep user on
 * their current branch if possible, else nearest" rule;
 * `strictBranchId` on the request disables the fallback for flows
 * where the user picked an exact branch and the flow must not silently
 * reassign them.
 */
export const resolveBrendaChargesPickup = (
  charges: GetPricesHireData,
  request: BrendaChargesSubmit
): BrendaChargesPickup => {
  const productType = inferBrendaProductType(charges)

  const activeBranch = request.currentBranchId
    ? findBrendaPickupByBranchId(
        charges,
        request.quantity,
        request.currentBranchId,
        request.strictBranchId
      )
    : findBrendaNearestPickup(charges, request.quantity)

  const stock = resolveBrendaPickupStock(
    productType,
    activeBranch?.quantity,
    activeBranch?.firstAvailableDate ?? null,
    request.startDate
  )

  return {
    available: stock.available,
    branchId: activeBranch?.id ?? null,
    branchName: activeBranch?.name ?? null,
    branchAddress: formatBrendaBranchAddress(activeBranch),
    branchAddressLine1: activeBranch?.siteLocation?.address1 ?? null,
    branchPostcode: activeBranch?.siteLocation?.postcode ?? null,
    branchDistance: formatBrendaBranchDistance(activeBranch?.distance),
    firstAvailableDate: activeBranch?.firstAvailableDate ?? null,
    date: stock.available ? stock.date : null,
    maxQuantity: stock.maxQuantity,
    stockIsFallback: stock.isFallback,
    linked: mapBrendaLinkedStock(activeBranch?.linkedProductAvailability),
  }
}

// ---------------------------------------------------------------------------
// Mandatory-linked delivery bump
// ---------------------------------------------------------------------------

/**
 * Walks the mandatory linked (hire + resale) products on a charges
 * response and, if any of them have a `firstAvailableDate` later than
 * the resolved delivery date, returns a new delivery summary with the
 * date pushed forward to match.
 *
 * Applied unconditionally to the delivery side so the `delivery.date`
 * callers see is always the true date the entire line can be delivered.
 * Pickup doesn't need this — linked stock is resolved per-branch by
 * `findBrendaPickupByBranchId`.
 */
const applyMandatoryLinkedDeliveryBump = (
  delivery: BrendaChargesDelivery,
  charges: GetPricesHireData
): BrendaChargesDelivery => {
  if (!delivery.available || !delivery.date) return delivery

  const mandatoryLinked = [
    ...(charges.linkedHireProducts ?? []),
    ...(charges.linkedResaleProducts ?? []),
  ].filter(linked => linked?.isMandatory)

  let bumpedDate = delivery.date

  for (const linked of mandatoryLinked) {
    const linkedStart = linked.firstAvailableDate

    if (linkedStart && linkedStart > bumpedDate) {
      bumpedDate = linkedStart
    }
  }

  if (bumpedDate === delivery.date) return delivery

  return { ...delivery, date: bumpedDate }
}

// ---------------------------------------------------------------------------
// Single-pass evaluation
// ---------------------------------------------------------------------------

/**
 * Turns a raw charges response + request into the single "evaluation"
 * the rest of the flow needs: resolved delivery, resolved pickup, the
 * chosen fulfilment, and any date correction. This is the one place
 * stock rules and date-shift decisions live — `buildBrendaChargesResult`
 * just wraps the evaluation in the final result shape, and the hook
 * reads `evaluation.correction` to decide whether to re-fetch.
 *
 * Flow:
 *   1. Resolve delivery via `resolveBrendaChargesDelivery`.
 *   2. Apply the mandatory-linked-delivery bump so `delivery.date`
 *      reflects the true earliest date the whole line can be delivered.
 *   3. Resolve pickup via `resolveBrendaChargesPickup` — note this
 *      uses the caller-supplied `currentBranchId` / `strictBranchId`
 *      so the branch matches what the final result will expose.
 *   4. Pick `chosenSource` by comparing the resolved `.date` values
 *      with the hire tie-break (pickup wins ties for `bulk`).
 *   5. Compute `correction` by comparing the chosen side's start date
 *      against the submitted one and preserving the requested duration.
 */
export const evaluateBrendaCharges = (
  charges: GetPricesHireData,
  request: BrendaChargesSubmit,
  bankHolidays: HssBankHolidayItem[] | null,
  /**
   * The user's *original* requested range (preserved across every
   * re-fetch / fulfilment-switch). Used as the source of truth for
   * the hire duration when computing a correction so the corrected
   * end date always anchors on the user's first-asked-for window
   * — never the previous submit's BH-bumped resolvedEndDate.
   */
  originalStartDate: string,
  originalEndDate: string
): BrendaChargesEvaluation => {
  const productType = inferBrendaProductType(charges)

  // Resolve both sides once. Delivery gets the mandatory-linked bump
  // applied here so downstream callers always see the accurate date.
  const rawDelivery = resolveBrendaChargesDelivery(charges, request)
  const delivery = applyMandatoryLinkedDeliveryBump(rawDelivery, charges)
  const pickup = resolveBrendaChargesPickup(charges, request)

  // Pick the earliest-available side. For hire (`bulk`) pickup wins
  // ties; for consumables pickup must be strictly earlier (pickup is
  // rarely available for consumables anyway, so this is a safety net).
  let chosenSource: 'delivery' | 'pickup' | null = null

  if (delivery.available && pickup.available && delivery.date && pickup.date) {
    const pickupWinsTies = productType === 'bulk'
    const pickupWins = pickupWinsTies
      ? pickup.date <= delivery.date
      : pickup.date < delivery.date

    chosenSource = pickupWins ? 'pickup' : 'delivery'
  } else if (delivery.available) {
    chosenSource = 'delivery'
  } else if (pickup.available) {
    chosenSource = 'pickup'
  }

  // Work out whether the submitted dates need to shift. For single-day
  // requests (start === end, i.e. consumables) we never touch dates.
  //
  // When the caller specified `selectedFulfilment`, correction anchors
  // on *that* side — so pricing/availability always end up on the
  // committed window. Otherwise it anchors on `chosenSource` (the
  // earliest-available side), preserving the pre-existing PDP
  // semantic. Falls back to `chosenSource` when the selected side
  // couldn't resolve (e.g. selected pickup but pickup not viable for
  // this address) so the correction path still fires a useful
  // re-fetch rather than giving up.
  let correction: { startDate: string; endDate: string } | null = null

  const selectedAvailable =
    request.selectedFulfilment === 'pickup'
      ? pickup.available
      : request.selectedFulfilment === 'delivery'
        ? delivery.available
        : false

  const effectiveSource: 'delivery' | 'pickup' | null =
    request.selectedFulfilment && selectedAvailable
      ? request.selectedFulfilment
      : chosenSource

  if (request.startDate !== request.endDate && effectiveSource) {
    const effectiveStart =
      effectiveSource === 'pickup' ? pickup.date : delivery.date

    // Anchor duration on the user's original range so corrections
    // never inherit a previously BH-bumped resolvedEndDate.
    const duration = checkBrendaDaysBetween(originalStartDate, originalEndDate)

    const correctedStart = effectiveStart ?? request.startDate
    const correctedEnd = chooseBrendaHireEndDate(
      duration,
      correctedStart,
      bankHolidays
    )

    // Trigger a re-fetch on two conditions:
    //   1. Brenda's resolved start differs from what we asked for
    //      (future-stock / firstAvailableDate shift).
    //   2. The submitted end date lands on a bank holiday once the
    //      holidays cache is populated. Callers compute the end up
    //      front using `chooseBrendaHireEndDate`, but the very first
    //      client-side submit on a session can race the BH fetch and
    //      slip a BH end date through. Recomputing here against the
    //      now-loaded holidays corrects it without the caller needing
    //      to guard every code path.
    const startShifted = effectiveStart && effectiveStart !== request.startDate
    const endShifted = correctedEnd !== request.endDate

    if (startShifted || endShifted) {
      correction = { startDate: correctedStart, endDate: correctedEnd }
    }
  }

  return { delivery, pickup, chosenSource, correction }
}

// ---------------------------------------------------------------------------
// Full result assembly
// ---------------------------------------------------------------------------

/**
 * Thin composition over a `BrendaChargesEvaluation` — wraps the
 * resolved availability, the product display envelope, and the
 * request/raw fields into the final consumer-facing result shape.
 * All stock / date rules live in `evaluateBrendaCharges`; this
 * function exists purely to produce the public shape.
 *
 * `originalStartDate` / `originalEndDate` are the first dates the user
 * asked for in the current `useBrendaCharges` session — preserved
 * across every internal re-fetch and fulfilment-switch re-submit so
 * the composed result always reflects the user's original range.
 */
export const buildBrendaChargesResult = (
  charges: GetPricesHireData,
  request: BrendaChargesSubmit,
  evaluation: BrendaChargesEvaluation,
  originalStartDate: string,
  originalEndDate: string
): BrendaChargesResult => {
  // Pass the resolved parent delivery date (post mandatory-linked
  // bump) so the mapper can gate linked add-ons against it rather
  // than against the raw request date — otherwise a legitimate bump
  // (parent shifts to 22nd because a mandatory needs 22nd) would
  // wrongly flag a 22nd extra as "later than parent".
  const product = mapBrendaChargesData(
    charges,
    request,
    evaluation.delivery.date
  )

  // Two date "lenses" on the same response:
  //
  //   - `earliest*` always reflects the side the evaluator picked as
  //     earliest-available (pickup wins ties for bulk). This is the
  //     original PDP semantic ("the soonest I could have this").
  //   - `resolved*` reflects the side the caller is committing to.
  //     When `request.selectedFulfilment` is set we read that side's
  //     date; otherwise we fall back to `earliest*` — so callers that
  //     haven't opted in see identical behaviour to before.
  //
  // Single-day requests (buy products) always end on the same day as
  // they start. Hire requests keep the post-correction end date from
  // the submit.
  const isSingleDay = request.startDate === request.endDate

  const earliestDate =
    evaluation.chosenSource === 'pickup'
      ? evaluation.pickup.date
      : evaluation.chosenSource === 'delivery'
        ? evaluation.delivery.date
        : null

  const earliestStartDate = earliestDate ?? request.startDate
  const earliestEndDate = isSingleDay ? earliestStartDate : request.endDate

  const selectedDate = request.selectedFulfilment
    ? request.selectedFulfilment === 'pickup'
      ? evaluation.pickup.date
      : evaluation.delivery.date
    : null

  // When the caller selected a side, prefer that side's date. Falls
  // back to `earliestDate` when the selected side didn't resolve
  // (e.g. pickup not viable for this address) so we never return a
  // null `resolvedStartDate` — downstream callers that care about
  // availability check `delivery.available` / `pickup.available`.
  const resolvedStartDate =
    (selectedDate ?? earliestDate) ?? request.startDate
  const resolvedEndDate = isSingleDay ? resolvedStartDate : request.endDate

  return {
    request: {
      originalStartDate,
      originalEndDate,
      resolvedStartDate,
      resolvedEndDate,
      earliestStartDate,
      earliestEndDate,
      // The dates the final (post-correction) API call was made for —
      // the branch stock in this response is valid for these.
      submittedStartDate: request.startDate,
      submittedEndDate: request.endDate,
      currentBranchId: request.currentBranchId ?? null,
      quantity: request.quantity,
      productCode: request.stockCode,
      userPostcode: request.postcode,
      selectedFulfilment: request.selectedFulfilment ?? null,
      earliestFulfilment: evaluation.chosenSource,
    },
    delivery: evaluation.delivery,
    pickup: evaluation.pickup,
    product,
    raw: charges,
  }
}
